import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../utils/api'; // axios instance
import { toast } from 'react-toastify';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get('token');
  const [email, setEmail] = useState('');
  const [groupId, setGroupId] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);

  // Decode invite token on mount
  useEffect(() => {
    if (!token) {
      toast.error('Invalid invite link');
      return;
    }
    try {
      // Decode base64 token (format: email|groupId)
      const decoded = atob(token);
      const [invitedEmail, gid] = decoded.split('|');
      setEmail(invitedEmail);
      setGroupId(gid);
    } catch {
      toast.error('Invalid invite token');
    }
  }, [token]);

  // Handle registration form submit
  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Register user (expect backend to return JWT token)
      const res = await api.post('/api/users/register', {
        username,
        email,
        password,
      });

      // Save JWT token to localStorage for authenticated calls
      localStorage.setItem('token', res.data.token);

      toast.success('Registered successfully!');
      setRegistered(true);

      // Now accept the invite using the stored token for auth
      await acceptInvite(groupId);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  // Call accept invite API (authenticated)
  const acceptInvite = async (gid) => {
    try {
      await api.post('/api/invites/accept', { groupId: gid });
      toast.success('You have successfully joined the group!');
      // Redirect to group page or home
      navigate(`/groups/view/${gid}`);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to accept invite');
    }
  };

  // Optional: redirect after registration success
  useEffect(() => {
    if (registered) {
      const timer = setTimeout(() => {
        navigate('/home');
      }, 5000); // fallback redirect if acceptInvite doesn't redirect
      return () => clearTimeout(timer);
    }
  }, [registered, navigate]);

  return (
    <div className="max-w-md mx-auto mt-8 p-4 border rounded shadow">
      {!registered ? (
        <>
          <h2 className="text-xl font-semibold mb-4">Accept Group Invitation</h2>
          <form onSubmit={handleRegister}>
            <div className="mb-3">
              <label>Email</label>
              <input
                type="email"
                value={email}
                readOnly
                className="border p-2 w-full bg-gray-200 cursor-not-allowed"
              />
            </div>
            <div className="mb-3">
              <label>Username</label>
              <input
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="border p-2 w-full"
              />
            </div>
            <div className="mb-3">
              <label>Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border p-2 w-full"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-green-600 text-white px-4 py-2 rounded w-full"
            >
              {loading ? 'Registering...' : 'Register'}
            </button>
          </form>
        </>
      ) : (
        <p className="text-center text-green-600">Registration successful! Redirecting...</p>
      )}
    </div>
  );
}
